import {
    clearAiAgentChatState,
    clearMessages,
    selectIsInitialChatState,
    selectUserMessageCount,
    setAgentMessage,
    setIsInitialChatState,
    type UserMessageData,
} from "app/pages/sales-intelligence/modules/ai-agent/store";
import type { AiThreadMessage, SendMessageResult } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import { selectFiltersValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/selectors";
import { createHasAppliedFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/store/utils/selectCombiners";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { selectSignalsById } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { aiAgentQueryKeys } from "../../../queries/queryKeyFactory";
import { systemMessageService } from "../../../services/system-message/SystemMessageService";
import { setSystemMessage, setUserMessage } from "../../../store/chat/effects";
import { type SystemMessageContext, SystemMessageType } from "../../../types/systemMessages";
import { validateLeadGeneratorThread } from "../queries/utils";
import { messageContentService } from "../services/message-content";
import { applicationStateSyncService } from "../services/state-sync";
import { leadGeneratorThreadService } from "../services/thread";
import leadGeneratorThreadPrefetchService from "../services/thread/threadPrefetchService.init";
import type { PocessedMessageData } from "../store/types";
import type { IApplicationStatePayload, LeadGeneratorAgentPayload } from "../types/dto";
import { preprocessMessageContent } from "../utils/preprocessMessageContent";

import { resetGeneratedQueries, setPreprocessedMessageData } from "./action-creators";
import { selectAreFiltersOutOfSync } from "./selectors";
import { withThreadValidation } from "./utils/effectUtils";

export const setLeadGeneratorSystemMessage =
    (
        type: SystemMessageType,
        context: SystemMessageContext | undefined,
        payload: Omit<LeadGeneratorAgentPayload, "message">,
    ) =>
    (dispatch: ThunkDispatchCommon): SendMessageResult => {
        const shouldSendPayload = systemMessageService.shouldSendPayload(type);

        return dispatch(setSystemMessage(type, context ?? {}, shouldSendPayload ? payload : null));
    };

/**
 * Seeds the agent's first system message on page load. The message TYPE is driven purely by whether the
 * filter set carries any applied selection: populated ⇒ `ExistingFilterSet` (contextual suggestions),
 * empty ⇒ `NewFilterSet` (fresh start).
 *
 * The filter SOURCE is route-agnostic: the caller (`useLeadGeneratorChatInit`) only fires this once
 * `isInitialFiltersReady` is set — i.e. after the route's terminal initial writer has committed the
 * leads slice — so reading `selectFiltersValue` here is race-free and matches the manual panel.
 * Re-entrant: only seeds an empty chat, so callers that reset the chat first (e.g. clearThreadContext)
 * can reuse it and re-read the current (committed) filters.
 */
export const setInitialFilterSetMessage =
    () =>
    async (
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        { si: { queryClient } }: ThunkDependencies,
    ): Promise<SendMessageResult | undefined> => {
        if (selectUserMessageCount(getState()) > 0) {
            return;
        }

        const savedSearchRoutingParams = siNavigator.getSavedSearchPageParams();

        return withThreadValidation((_threadId: string) => {
            const filters = selectFiltersValue(getState());

            const type = createHasAppliedFilters(filters)
                ? SystemMessageType.ExistingFilterSet
                : SystemMessageType.NewFilterSet;

            return dispatch(
                setLeadGeneratorSystemMessage(type, undefined, {
                    inputFiltersState: filters,
                }),
            );
        }, savedSearchRoutingParams)(queryClient);
    };

export const setApplicationStateChangeMessage =
    (payload: IApplicationStatePayload) =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { queryClient } }: ThunkDependencies,
    ): Promise<SendMessageResult | undefined> => {
        const savedSearchRoutingParams = siNavigator.getSavedSearchPageParams();

        return withThreadValidation(
            (_threadId: string) =>
                dispatch(setLeadGeneratorSystemMessage(SystemMessageType.ApplicationStateChange, undefined, payload)),
            savedSearchRoutingParams,
        )(queryClient);
    };

export const setFiltersStateChangeMessage =
    () =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState): Promise<SendMessageResult | undefined> =>
        dispatch(
            setApplicationStateChangeMessage(
                applicationStateSyncService.createFiltersStateChangePayload(selectFiltersValue(getState())),
            ),
        );

export const setSyncFilterManualChangeMessage =
    () =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState): Promise<SendMessageResult | undefined> => {
        const state = getState();
        const areFiltersDifferentFromLastSent = selectAreFiltersOutOfSync(state);

        // Only sync if filters are out of sync with output AND different from what was last sent
        if (!areFiltersDifferentFromLastSent) {
            return;
        }

        return dispatch(setFiltersStateChangeMessage());
    };

export const setFilterRegenrateSuggestionsMessage =
    () =>
    async (
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        { si: { queryClient } }: ThunkDependencies,
    ): Promise<SendMessageResult | undefined> => {
        const savedSearchRoutingParams = siNavigator.getSavedSearchPageParams();

        return withThreadValidation((_threadId: string) => {
            const filterValues = selectFiltersValue(getState());

            dispatch(clearMessages());
            dispatch(
                setLeadGeneratorSystemMessage(SystemMessageType.RegenerateSuggestions, undefined, {
                    inputFiltersState: filterValues,
                }),
            );

            return { success: true };
        }, savedSearchRoutingParams)(queryClient);
    };

export const setLeadGeneratorUserMessage =
    <Payload extends UserMessageData = UserMessageData>(payload: Partial<Payload>) =>
    (
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        { si: { queryClient } }: ThunkDependencies,
    ): SendMessageResult => {
        let result: SendMessageResult;

        const isInitialChatState = selectIsInitialChatState(getState());
        const savedSearchRoutingParams = siNavigator.getSavedSearchPageParams();

        const validation = validateLeadGeneratorThread(queryClient, savedSearchRoutingParams);

        if (!validation.isValid || validation.numberOfFetchingQueries > 0) {
            result = { success: false };
        } else {
            result = dispatch(setUserMessage(payload, isInitialChatState));
        }

        if (result.success && isInitialChatState) {
            dispatch(setIsInitialChatState(false));
            dispatch(resetGeneratedQueries());
        }

        return result;
    };

export const setLeadGeneratorAgentMessage =
    (payload: Omit<AiThreadMessage, "type">) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState): Promise<PocessedMessageData | null> => {
        let proccessedMessageData: PocessedMessageData | null = null;

        const state = getState();

        const isInitialChatState = selectIsInitialChatState(state);

        const result = dispatch(setAgentMessage(payload, isInitialChatState));

        if (result.success) {
            const preprocessedData = await preprocessMessageContent(payload.content);

            const signalsById = selectSignalsById(state);

            const enrichedJsonObjects = messageContentService.enrichMessageJsonObjects(preprocessedData.jsonObjects, {
                signalsById,
            });

            proccessedMessageData = {
                messageId: payload.id,
                jsonObjects: enrichedJsonObjects,
                commands: preprocessedData.commands,
            };

            dispatch(setPreprocessedMessageData(proccessedMessageData));

            if (isInitialChatState) {
                dispatch(setIsInitialChatState(false));
                dispatch(resetGeneratedQueries());
            }
        }

        return proccessedMessageData;
    };

export const clearThreadContext =
    () =>
    async (
        dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        { si: { queryClient } }: ThunkDependencies,
    ): Promise<SendMessageResult | undefined> => {
        const { savedSearchId } = siNavigator.getSavedSearchPageParams() ?? {};

        const threadId = queryClient.getQueryData<string>(aiAgentQueryKeys.leadGenerator.thread(savedSearchId));

        if (threadId) {
            dispatch(clearAiAgentChatState());

            leadGeneratorThreadService.clearPersistedThreadWithOptions({ savedSearchId });

            queryClient.removeQueries({
                queryKey: aiAgentQueryKeys.leadGenerator.chat(threadId),
            });

            void queryClient.resetQueries({
                queryKey: aiAgentQueryKeys.leadGenerator.thread(savedSearchId),
            });
        } else {
            await leadGeneratorThreadPrefetchService.prefetchThread({ savedSearchId });
        }

        return dispatch(setInitialFilterSetMessage());
    };

/**
 * Resets the leadgen agent thread only when one already exists for the current context. Unlike
 * {@link clearThreadContext}, this never eagerly creates a thread: with none cached there is nothing
 * stale to discard, and the next agent open will create a fresh one. Used by the industry v2 opt-in
 * fan-out, where enrolling can happen off the leadgen page.
 */
export const resetLeadGeneratorThreadIfPresent =
    () =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies): Promise<void> => {
        const { savedSearchId } = siNavigator.getSavedSearchPageParams() ?? {};

        const threadId = deps.si.queryClient.getQueryData<string>(aiAgentQueryKeys.leadGenerator.thread(savedSearchId));

        if (!threadId) {
            return;
        }

        await clearThreadContext()(dispatch, getState, deps);
    };
